import * as React from 'react'

import type { UseFormReturn } from 'react-hook-form'

import { getResourceDefinition } from '~/client/src/shared/resources/definitions'

import type { FieldConfig } from '../DynamicForm/types'

/**
 * A resource whose fields derive from one another (a location's icon follows its kind)
 * declares that in its definition. Applied on user edits only, and only to fields the
 * form is showing and the user may change, so a derivation cannot smuggle in a value
 * nobody saw or one the user is locked out of (a member's location icon).
 */
export function useDeriveFieldUpdates(
  formMethods: UseFormReturn<Record<string, unknown>>,
  resourceTypeId: string,
  editableFields: FieldConfig[],
): void {
  const deriveFieldUpdates =
    getResourceDefinition(resourceTypeId)?.form?.deriveFieldUpdates
  const editableFieldNames = React.useMemo(
    () =>
      new Set(
        editableFields
          .filter(field => !field.disabled && !field.displayOnly)
          .map(field => field.name),
      ),
    [editableFields],
  )

  React.useEffect(() => {
    if (!deriveFieldUpdates) return

    // The values before each change, so a derivation can tell what an edit replaced.
    let previousValues = { ...formMethods.getValues() }
    const subscription = formMethods.watch((values, { name, type }) => {
      const before = previousValues
      previousValues = { ...values }
      if (!name || type !== 'change') return

      const updates = deriveFieldUpdates(name, values[name], values, before)
      if (!updates) return

      Object.entries(updates).forEach(([field, value]) => {
        if (editableFieldNames.has(field)) {
          formMethods.setValue(field, value, { shouldDirty: true })
        }
      })
    })

    return () => subscription.unsubscribe()
  }, [formMethods, deriveFieldUpdates, editableFieldNames])
}
